import api, { API_BASE_URL } from './api';
import {
  OhlcvResponse, Position, TradeRecord, PnlSummary, PnlHistoryPoint,
  AgentPortfolio, ExchangeCredential, ExchangeCredentialCreate, SupportedExchange,
  CurrentPriceResponse,
} from '../types/trading';

const API_BASE = API_BASE_URL;

// -------------------------------------------------------------------------
// OHLCV / market data
// -------------------------------------------------------------------------

export async function getOhlcv(params: {
  symbol: string;
  exchange: string;
  timeframe?: string;
  limit?: number;
  agent_id?: string;
  is_paper?: boolean;
}): Promise<OhlcvResponse> {
  const { data } = await api.get(`${API_BASE}/trading/ohlcv`, { params });
  return data;
}

// -------------------------------------------------------------------------
// Positions
// -------------------------------------------------------------------------

export async function getPositions(params?: {
  agent_id?: string;
  status?: 'open' | 'closed' | 'all';
  is_paper?: boolean;
}): Promise<Position[]> {
  const { data } = await api.get(`${API_BASE}/trading/positions`, { params });
  return data;
}

export async function refreshPositionPrices(): Promise<{ updated: number }> {
  const { data } = await api.post(`${API_BASE}/trading/positions/refresh`);
  return data;
}

export async function closePosition(
  positionId: string,
  exitPrice?: number,
): Promise<{ position_id: string; closed: boolean; exit_price: number; realized_pnl: number }> {
  const { data } = await api.post(`${API_BASE}/trading/positions/${positionId}/close`, {
    exit_price: exitPrice ?? null,
  });
  return data;
}

// -------------------------------------------------------------------------
// P&L
// -------------------------------------------------------------------------

export async function getPnlSummary(params?: {
  agent_id?: string;
  is_paper?: boolean;
}): Promise<PnlSummary> {
  const { data } = await api.get(`${API_BASE}/trading/pnl`, { params });
  return data;
}

export async function getPnlHistory(params?: {
  agent_id?: string;
  is_paper?: boolean;
  limit?: number;
}): Promise<PnlHistoryPoint[]> {
  const { data } = await api.get(`${API_BASE}/trading/pnl/history`, { params });
  return data;
}

// -------------------------------------------------------------------------
// Trades
// -------------------------------------------------------------------------

export async function getTrades(params?: {
  agent_id?: string;
  symbol?: string;
  is_paper?: boolean;
  status?: string;
  limit?: number;
}): Promise<TradeRecord[]> {
  const { data } = await api.get(`${API_BASE}/trading/trades`, { params });
  return data;
}

// -------------------------------------------------------------------------
// Portfolio
// -------------------------------------------------------------------------

export async function getPortfolio(params?: { is_paper?: boolean }): Promise<AgentPortfolio[]> {
  const { data } = await api.get(`${API_BASE}/trading/portfolio`, { params });
  return data;
}

// -------------------------------------------------------------------------
// Current price
// -------------------------------------------------------------------------

export async function getCurrentPrice(params: {
  symbol: string;
  exchange: string;
}): Promise<CurrentPriceResponse> {
  const { data } = await api.get(`${API_BASE}/trading/price`, { params });
  return data;
}

// -------------------------------------------------------------------------
// Exchange credentials
// -------------------------------------------------------------------------

export async function listExchangeCredentials(): Promise<ExchangeCredential[]> {
  const { data } = await api.get(`${API_BASE}/exchanges`);
  return data;
}

export async function createExchangeCredential(
  body: ExchangeCredentialCreate,
): Promise<ExchangeCredential> {
  const { data } = await api.post(`${API_BASE}/exchanges`, body);
  return data;
}

export async function updateExchangeCredential(
  id: string,
  body: Partial<ExchangeCredentialCreate & { is_active: boolean }>,
): Promise<ExchangeCredential> {
  const { data } = await api.put(`${API_BASE}/exchanges/${id}`, body);
  return data;
}

export async function deleteExchangeCredential(id: string): Promise<void> {
  await api.delete(`${API_BASE}/exchanges/${id}`);
}

export async function testExchangeCredential(
  id: string,
): Promise<{ success: boolean; message: string; account: any }> {
  const { data } = await api.post(`${API_BASE}/exchanges/${id}/test`);
  return data;
}

export async function getExchangeBalance(id: string): Promise<any> {
  const { data } = await api.get(`${API_BASE}/exchanges/${id}/balance`);
  return data;
}

export async function listSupportedExchanges(): Promise<SupportedExchange[]> {
  const { data } = await api.get(`${API_BASE}/exchanges-supported`);
  return data;
}
